import { Check } from 'lucide-react'
import { cn } from '@/app/lib/cn'

/**
 * The progress rail. It is the console's primary orientation cue: an operator
 * glancing at the screen mid-conversation should know which decision they are
 * on without reading anything.
 *
 * Completed steps stay tappable so going back is one touch, but steps ahead are
 * not — you cannot skip choosing photos and land on Print.
 */
export interface StepDef {
  id: string
  label: string
}

export function Stepper({
  steps,
  current,
  onGoTo,
}: {
  steps: StepDef[]
  current: number
  onGoTo?: (index: number) => void
}) {
  return (
    <nav aria-label="Progress" className="flex items-center gap-1 sm:gap-2">
      {steps.map((step, i) => {
        const done = i < current
        const active = i === current
        const reachable = done && onGoTo

        return (
          <div key={step.id} className="flex items-center gap-1 sm:gap-2">
            {i > 0 && (
              <span
                aria-hidden
                className={cn(
                  'h-px w-4 sm:w-10',
                  done ? 'bg-brand' : 'bg-line',
                )}
              />
            )}
            <button
              type="button"
              disabled={!reachable}
              onClick={() => reachable && onGoTo(i)}
              aria-current={active ? 'step' : undefined}
              className={cn(
                'flex min-h-[44px] items-center gap-2 rounded-[999px] px-3 sm:px-4',
                'text-[14px] font-medium transition-colors',
                active && 'bg-brand text-on-brand',
                done && 'text-brand',
                !active && !done && 'text-ink-faint',
                reachable && 'active:bg-brand-dim',
              )}
            >
              <span
                className={cn(
                  'grid h-6 w-6 shrink-0 place-items-center rounded-full text-[12px] font-bold',
                  active && 'bg-on-brand/20 text-on-brand',
                  done && 'bg-brand text-on-brand',
                  !active && !done && 'ring-1 ring-line-strong',
                )}
              >
                {done ? <Check size={14} strokeWidth={3} /> : i + 1}
              </span>
              <span className="hidden sm:inline">{step.label}</span>
            </button>
          </div>
        )
      })}
    </nav>
  )
}
